import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { DrawerService } from '../../../../../core/services/drawer.service';
import {
  BlockValueModel,
  DatasheetDataItem,
  DataSheetResult,
  EnterDataDrawerParams,
  FequencyTimeSlots,
  UnitMultiValue,
} from '../insights-data.interface';
import { finalize, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { NzTableModule } from 'ng-zorro-antd/table';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { EnterDataHeadingComponent } from './enter-data-heading/enter-data-heading.component';
import {
  NzDatePickerComponent,
  NzDatePickerModule,
} from 'ng-zorro-antd/date-picker';
import { CommonModule } from '@angular/common';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { FormsModule } from '@angular/forms';
import { FrequencyComponent } from './frequency/frequency.component';
import { CustomTimeService } from '../../../../manage-settings/custom-time/custom-time.service';
import { ErrorResponse } from '../../../../../core/models/error.models';
import { LoaderService } from '../../../../../core/services/loader.service';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { AllowOnlyValidNumbersDirective } from '../../../../../core/direactives/allowedOnlyValidNUmber.directive';
import { CommonService } from '../../../../../shared/services/common.service';
import { endOfWeek, isAfter, startOfDay, startOfWeek } from 'date-fns';
import {
  OrgScheduleList,
  TimeSlot,
} from '../../../../manage-settings/manage-settings.interface';
import { InsightsDataService } from '../insights-data.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { SetDurationComponent } from './set-duration/set-duration.component';
import { PartialIntervalComponent } from './partial-interval/partial-interval.component';
import _ from 'lodash';
import { ConfirmDialogComponent } from '../../../../../components/confirm-dialog/confirm-dialog.component';
import { ManageDataNotesComponent } from './manage-data-notes/manage-data-notes.component';
import { TimeZoneService } from '../../../../../core/services/timezone.service';
import { AuditContextService } from '../../../../audit-logs/audit-context.service';
import { DrawerHeaderComponent } from '../../../../../components/drawer-header/drawer-header.component';
import { AuthService } from '../../../../../core/services/auth.service';
import { CmsRoleAccessService } from '../../../../../core/services/cms-role-access.service';
import { UserRole } from '../../../../activity-dashboard/activity-dashboard.enums';

@Component({
  selector: 'app-enter-data',
  standalone: true,
  imports: [
    EnterDataHeadingComponent,
    NzDatePickerModule,
    NzTableModule,
    CommonModule,
    NzButtonModule,
    NzInputModule,
    FormsModule,
    ConfirmDialogComponent,
    NzSpinModule,
    NgxMaskDirective,
    AllowOnlyValidNumbersDirective,
    DrawerHeaderComponent,
  ],
  providers: [
    provideNgxMask(), // Starts week on Sunday
  ],
  templateUrl: './enter-data.component.html',
  styleUrl: './enter-data.component.scss',
})
export class EnterDataComponent implements OnInit, OnDestroy {
  @ViewChild('weekPicker') weekPicker!: NzDatePickerComponent;
  @ViewChild('drawerTitleFrequency')
  drawerTitleFrequency!: TemplateRef<void>;
  @ViewChild('drawerTitleDuration')
  drawerTitleDuration!: TemplateRef<void>;
  @ViewChild('drawerTitlePartialInterval')
  drawerTitlePartialInterval!: TemplateRef<void>;
  @ViewChild('drawerTitleDataNotes')
  drawerTitleDataNotes!: TemplateRef<void>;

  private readonly destroy$ = new Subject<void>();
  loading$: Observable<boolean> = of(false);
  drawerParams: EnterDataDrawerParams = {} as EnterDataDrawerParams;
  weekDays: { date: string; day: string }[] = [];
  pageNumber = 1;
  totalPages = 100;
  selectedRange: Date | null = null; // Changed back to selectedRange for week selection
  dataSheetDataItems: DatasheetDataItem[] = [] as DatasheetDataItem[];
  scheduleData: OrgScheduleList = {} as OrgScheduleList;
  isModify = false;
  lastDateOfCurrentWeek!: Date;
  behaviorData: DataSheetResult = {} as DataSheetResult;
  showConfirmDialog = false;
  isDeleting = false;
  deleteMsg = '';
  deleteObj: {
    id: number;
    date: string;
  } = {
    id: 0,
    date: '',
  };
  weekRange: Date[] = [];
  rangeErrorMsg = '';
  userRoles: string[] = [];
  roleEnum = UserRole;
  allowDeleteDataEntry = true;
  constructor(
    private readonly drawerService: DrawerService,
    private readonly customService: CustomTimeService,
    private readonly loaderService: LoaderService,
    private readonly commonService: CommonService,
    private readonly insightsService: InsightsDataService,
    private readonly cdr: ChangeDetectorRef,
    private readonly timeZoneService: TimeZoneService,
    private readonly auditContextService: AuditContextService,
    private readonly authService: AuthService,
    private readonly cmsRoleAccessService: CmsRoleAccessService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.authService.userRoles$
      .pipe(takeUntil(this.destroy$))
      .subscribe(roles => {
        if (roles) {
          this.userRoles = roles;
          this.allowDeleteDataEntry =
            this.cmsRoleAccessService.computeAllowFromCmsRole(this.userRoles, [
              this.roleEnum.CMSDataEntry,
              this.roleEnum.CMSBrowser,
            ]);
        }
      });
    this.setupDrawerSubscription();
  }

  currentWeekStart!: string;

  ngOnInit() {
    // Set weekRange to [Sunday, Saturday] of the current week
    const today = new Date();
    const sunday = startOfWeek(today);
    const saturday = endOfWeek(today);
    this.weekRange = [sunday, saturday];
    this.onWeekRangeChange(this.weekRange);
    // nz-range-picker validates both sides of the pair against nzDisabledDate
    // when the panel closes. Saturday of the current week can be a future,
    // disabled date, which then blocks any later partial re-selection until
    // this end date is replaced by a valid one. Keep the bound value in sync
    // with disableFutureDates from the start.
    this.weekRange = [sunday, this.clampToToday(saturday)];
    this.initializeWeeks();
    this.setLastDateOfCurrentWeek();
    this.getDataSheetData();
  }

  private clampToToday(date: Date): Date {
    const today = startOfDay(new Date());
    return isAfter(date, today) ? today : date;
  }

  initializeWeeks() {
    const today = new Date();
    const sunday = startOfWeek(today);
    const saturday = endOfWeek(today);
    this.weekDays = this.getCurrentWeekDates(sunday, saturday);

    this.currentWeekStart = this.weekDays[0].date; // Store the first day (Sunday) of the current week
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<EnterDataDrawerParams>(DRAWER_IDS.ENTER_DATA)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.drawerParams = params;
          this.getScheduleData();
        }
      });
  }
  setLastDateOfCurrentWeek() {
    const today = new Date();
    this.lastDateOfCurrentWeek = endOfWeek(today); // Get last day of current week (Saturday)
  }

  onChange(selectedDate: Date): void {
    if (!selectedDate) {
      this.selectedRange = null;
      return;
    }
    this.isModify = false;
    const sunday = startOfWeek(selectedDate);
    const saturday = endOfWeek(selectedDate);
    this.weekDays = this.getCurrentWeekDates(sunday, saturday);
    this.selectedRange = new Date(this.weekDays[0].date);
    this.getDataSheetData();
  }

  // Updated to only disable future dates (not weekends)
  disableFutureDates = (current: Date): boolean => {
    const today = startOfDay(new Date()); // Get today without time
    return isAfter(current, today); // Only disable future dates
  };

  getCurrentWeekDates(start: Date, end: Date): { date: string; day: string }[] {
    this.dataSheetDataItems = [];
    const weekDates = this.commonService.getRangeDates(start, end);
    for (const element of weekDates) {
      // Use the date string directly as it's already in YYYY-MM-DD format
      this.dataSheetDataItems.push({
        id: 0,
        date: element.date,
        day: element.day,
        individualIntervalDetails: '',
        totalFrequency: '',
        totalDuration: '',
        averageDuration: '',
        action: '',
        isFrequencyValue: false,
        isDuration: false,
        isUpdateTotal: false,
        isFrequencyOnly: false,
        commentCount: 0,
      });
    }
    this.selectedRange = new Date(weekDates[0].date);
    return weekDates;
  }

  get getGrandTotal() {
    return _.sumBy(
      this.dataSheetDataItems,
      item => Number(item.totalFrequency) || 0
    );
  }
  get getTotalOfDuration() {
    return _.sumBy(
      this.dataSheetDataItems,
      item => Number(item.totalDuration) || 0
    );
  }
  get getTotalforAverage() {
    return _.sumBy(
      this.dataSheetDataItems,
      item => Number(item.averageDuration) || 0
    );
  }

  get filteredDataSheetDataItems() {
    return this.dataSheetDataItems.filter(
      d => d.day !== 'Sunday' && d.day !== 'Saturday'
    );
  }

  getScheduleData() {
    if (this.drawerParams.caseDetail.student.customScheduleId) {
      this.loaderService.show();
      this.customService
        .getOrgSechduleById(
          this.drawerParams.caseDetail.student.customScheduleId
        )
        .pipe(
          take(1), // Take only one response
          finalize(() => this.loaderService.hide()), // End loading after response
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: items => {
            this.scheduleData = items[0];
          },
          error: (error: ErrorResponse) => {
            this.loaderService.hide();
            this.commonService.notify('error', error.message);
          },
        });
    }
  }

  getDataSheetData() {
    const today = new Date();

    this.loaderService.show();
    this.insightsService
      .getDataSheetData({
        CaseBehaviorId: this.drawerParams.caseBehaviorId,
        UnitName: this.drawerParams.selectedUnit,
        StartDate: this.weekDays[0].date,
        EndDate: today.toISOString().split('T')[0], // Use .at(-1) for last element, fallback to ''
      })
      .pipe(
        take(1), // Take only one response
        finalize(() => this.loaderService.hide()), // End loading after response
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: response => {
          if (response?.result) {
            this.behaviorData = response.result;
            this.cdr.detectChanges();

            this.dataSheetDataItems = this.dataSheetDataItems.map(weekDay => {
              let totalFrequency = '';
              let individualIntervalDetails = '';
              let isFrequencyValue = false;
              let isFrequencyOnly = false;
              let isDuration = false;
              let totalDuration = weekDay.totalDuration;
              let averageDuration = weekDay.averageDuration;
              let id = 0;
              this.cdr.detectChanges();

              const {
                activityDataUnitValues,
                activityDataUnitMultiValues,
                commentCounts,
              } = this.behaviorData;

              // Handle activityDataUnitValues
              if (activityDataUnitValues.length > 0) {
                const matchedActivity = activityDataUnitValues.find(
                  activity => activity.dataDate === weekDay.date
                );
                if (matchedActivity) {
                  totalFrequency = matchedActivity.blockValue.toString();
                  id = matchedActivity.id;
                  isFrequencyValue = true;
                  isFrequencyOnly = true;
                }
              }

              if (activityDataUnitMultiValues.length > 0) {
                const matchedActivities = this.getFilteredActivities(
                  activityDataUnitMultiValues,
                  weekDay.date
                );
                if (matchedActivities.length > 0) {
                  isFrequencyValue = true;
                  totalFrequency =
                    this.getTotalFrequencyValue(matchedActivities);
                  individualIntervalDetails = this.getIntervalDetails(
                    matchedActivities,
                    weekDay
                  );
                }

                const matchedDurations = this.getFilteredDurations(
                  activityDataUnitMultiValues,
                  weekDay.date
                );
                if (matchedDurations.length > 0) {
                  isDuration = true;
                  totalFrequency =
                    this.getTotalFrequency(matchedDurations).toString();
                  totalDuration =
                    this.getTotalDuration(matchedDurations).toString();
                  averageDuration =
                    this.getAvgDuration(matchedDurations).toString();
                  individualIntervalDetails =
                    this.getDurationIntervals(matchedDurations).toString();
                }

                const matchedPartialInterval = this.getFilteredPartialIntervals(
                  activityDataUnitMultiValues,
                  weekDay.date
                );
                if (matchedPartialInterval.length > 0) {
                  isFrequencyValue = true;
                  individualIntervalDetails = this.getPartialIntervalDetails(
                    matchedPartialInterval
                  );
                }
              }
              // Add commentCount if available
              let commentCount = 0;
              if (Array.isArray(commentCounts)) {
                // Normalize both dates to 'yyyy-MM-dd' for comparison
                const weekDayDate =
                  weekDay.date.length > 10
                    ? weekDay.date.substring(0, 10)
                    : weekDay.date;
                const found = commentCounts.find(c => {
                  const commentDate =
                    c.date.length > 10 ? c.date.substring(0, 10) : c.date;
                  return commentDate === weekDayDate;
                });
                if (found) {
                  commentCount = found.count;
                }
              }
              const oldTotalFrequency = totalFrequency;
              return {
                ...weekDay,
                totalFrequency,
                individualIntervalDetails,
                isFrequencyValue,
                isFrequencyOnly,
                totalDuration,
                averageDuration,
                isDuration,
                oldTotalFrequency,
                id,
                commentCount,
              };
            });

            this.cdr.detectChanges();
          }
        },
        error: (error: ErrorResponse) => {
          this.loaderService.hide();
          this.commonService.notify('error', error.message);
        },
      });
  }

  // Helper functions
  getFilteredActivities(data: UnitMultiValue[], date: string) {
    return data.filter(
      activity =>
        activity.dataDate === date &&
        !activity.isDurationValue &&
        !activity.isIntervalFrequency
    );
  }

  getTotalFrequencyValue(matchedActivities: UnitMultiValue[]) {
    return _.sumBy(matchedActivities, activity =>
      Number(activity.unitValue)
    ).toString();
  }

  getIntervalDetails(
    matchedActivities: UnitMultiValue[],
    weekDay: DatasheetDataItem
  ) {
    const timeSlots = this.insightsService.generateTimeSlots(matchedActivities);
    return this.drawerParams.selectedUnit === 1 ||
      this.drawerParams.selectedUnit === 4
      ? this.getIndividualIntervalDetails(matchedActivities, weekDay, timeSlots)
      : matchedActivities.map(activity => activity.unitValue).join(', ');
  }

  getFilteredDurations(data: UnitMultiValue[], date: string) {
    return data.filter(
      activity =>
        activity.dataDate === date &&
        activity.isDurationValue &&
        !activity.isIntervalFrequency
    );
  }

  getFilteredPartialIntervals(data: UnitMultiValue[], date: string) {
    return data.filter(
      activity =>
        activity.dataDate === date &&
        !activity.isDurationValue &&
        activity.isIntervalFrequency
    );
  }

  //getInterval String
  getIndividualIntervalDetails(
    matchedActivities: UnitMultiValue[],
    weekDay: DatasheetDataItem,
    timeSlots: TimeSlot[] | FequencyTimeSlots[]
  ) {
    const unitArr: string[] = timeSlots.map(slot => {
      // Ensure start and end times match the expected format
      const formattedStartTime = `${slot.startTime}:00`;
      const formattedEndTime = `${slot.endTime}:00`;

      // Find a matching activity
      const matchedActivity = matchedActivities.find(
        ele =>
          ele.startDataTime === formattedStartTime &&
          ele.endDataTime === formattedEndTime &&
          ele.dataDate === weekDay.date
      );

      // If a match is found, return unitValue; otherwise, return empty string
      return this.getUnitValueOrDefault(matchedActivity);
    });

    return unitArr.join(',');
  }

  private getUnitValueOrDefault(
    matchedActivity: UnitMultiValue | undefined
  ): string {
    if (!matchedActivity) {
      return '-';
    }

    return matchedActivity.unitValue ? matchedActivity.unitValue : '-';
  }

  getDurationIntervals(matchedDurations: UnitMultiValue[]) {
    return _.chain(matchedDurations)
      .map(item => {
        // Convert unitValue into an array of numbers (ignoring empty values)
        const sum = _.chain(item.unitValue)
          .split(',') // Split by commas
          .map(_.trim) // Trim spaces
          .filter(val => val !== '') // Remove empty values
          .map(_.toNumber) // Convert to numbers
          .filter(_.isFinite) // Remove NaN values
          .sum() // Sum all numbers
          .value();

        // Check if any value is '0' (after trimming)
        const hasZero = _.chain(item.unitValue)
          .split(',')
          .map(_.trim)
          .includes('0')
          .value();

        return sum === 0 && !hasZero ? '-' : sum;
      })
      .join(',') // Join all sums with commas
      .value();
  }

  getPartialIntervalDetails(matchedActivities: UnitMultiValue[]) {
    return _.chain(matchedActivities)
      .map('unitValue') // Extract all unitValue strings
      .map(val => _.split(val, ',')) // Split each unitValue by comma
      .flatten() // Flatten nested arrays into a single array
      .map(val => (val.trim() === '' ? '-' : val)) // Replace empty values with '-'
      .join(',') // Join back into a comma-separated string
      .value();
  }
  //get Avg PT
  getAvgPartialInterval(data: string) {
    // Split the string and convert to numbers, treating empty values as null or undefined
    const unitValueArray = _.chain(data)
      .split(',') // Split string into an array by commas
      .map(val => _.trim(val)) // Trim spaces around values
      .filter(val => val !== '-' && val !== '') // Remove blank values
      .map(_.toNumber) // Convert remaining values to numbers
      .filter(_.isFinite) // Remove NaN values
      .value();

    if (unitValueArray.length === 0) {
      return '';
    }

    if (unitValueArray.every(value => value === 0)) {
      return 0;
    }

    const avg = _.sum(unitValueArray) / unitValueArray.length;

    // Apply rounding rule
    if (Number.isNaN(avg)) {
      return '';
    }
    if (avg % 1 === 0) {
      return avg;
    }
    const roundedAvg = Math.floor(avg) + (avg % 1 >= 0.5 ? 1 : 0);
    return roundedAvg;
  }

  // get TOtal duration
  getTotalDuration(data: UnitMultiValue[]) {
    return _.chain(data)
      .map('unitValue') // Extract all unitValue strings
      .map(val => _.split(val, ',')) // Split each unitValue by comma
      .flatten() // Flatten nested arrays into a single array
      .map(val => _.toNumber(val)) // Convert to numbers (empty strings become NaN)
      .filter(val => !_.isNaN(val)) // Remove NaN values
      .sum() // Get the sum of all values
      .value();
  }

  //get avg duration
  getAvgDuration(data: UnitMultiValue[]) {
    // Extract number of non-blank values from the unitValue string
    const nonBlankUnitValues = _.flatMap(
      data,
      item =>
        item.unitValue
          .split(',') // Split the string by commas
          .map(value => value.trim()) // Trim spaces around values
          .filter(value => value !== '' && value !== '0') // Filter out empty values and zeros
    ).length;
    const sumOfNonBlankUnitValues = _.sum(
      _.flatMap(
        data,
        item =>
          item.unitValue
            .split(',') // Split the string by commas
            .map(value => value.trim()) // Remove extra spaces around values
            .filter(value => value !== '' && value !== '0') // Filter out empty, blank values, and zeros
            .map(value => _.toNumber(value)) // Convert the non-empty values to numbers
      )
    );
    const avgUnitValue = sumOfNonBlankUnitValues / nonBlankUnitValues;

    if (
      Number.isNaN(avgUnitValue) ||
      avgUnitValue === 0 ||
      nonBlankUnitValues === 0
    ) {
      return '';
    }

    return this.roundAvgValue(avgUnitValue);
  }

  private roundAvgValue(value: number): number {
    if (value % 1 === 0) {
      return value;
    }
    return Math.floor(value) + (value % 1 > 0.5 ? 1 : 0);
  }

  getTotalFrequency(data: UnitMultiValue[]) {
    const nonZeroValues = _.flatMap(data, item =>
      item.unitValue
        .split(',') // Split the string by commas
        .map(value => value.trim()) // Trim any surrounding spaces
        .map(value => _.toNumber(value))
        .filter(value => _.isFinite(value) && value !== 0)
    );

    return nonZeroValues.length;
  }

  isTotalFrequencyEdit(data: DatasheetDataItem) {
    return (
      this.isModify && this.isFutureDate(data.date) && data.isFrequencyValue
    );
  }
  openSetFrequencyDrawer(data: DatasheetDataItem): void {
    this.drawerService
      .openDrawer(
        FrequencyComponent,
        this.drawerTitleFrequency as unknown as TemplateRef<object>,
        1170,
        true,
        undefined,
        {
          caseDetail: this.drawerParams.caseDetail,
          selectedUnit: this.drawerParams.selectedUnit,
          selectedDate: data.date,
          selectedDay: data.day,
          scheduleData: this.scheduleData,
          unitId: this.behaviorData.id,
          behaviorData: this.behaviorData,
          selectedRow: data,
          caseBehaviorId: this.drawerParams.caseBehaviorId,
          selectedUnitName: this.drawerParams.selectedUnitName,
          onDataSaved: () => this.getDataSheetData(),
          frequencyDrawerTitleTemplate: this
            .drawerTitleFrequency as unknown as TemplateRef<object>,
          durationDrawerTitleTemplate: this
            .drawerTitleDuration as unknown as TemplateRef<object>,
        },
        DRAWER_IDS.FREQUENCY
      )
      .afterClose.subscribe(() => {
        this.getDataSheetData();
      });
  }

  openSetDurationDrawer(data: DatasheetDataItem) {
    this.drawerService
      .openDrawer(
        SetDurationComponent,
        this.drawerTitleDuration as unknown as TemplateRef<object>,
        1170,
        true,
        undefined,
        {
          caseDetail: this.drawerParams.caseDetail,
          selectedUnit: this.drawerParams.selectedUnit,
          selectedDate: data.date,
          selectedDay: data.day,
          scheduleData: this.scheduleData,
          unitId: this.behaviorData.id,
          caseBehaviorId: this.drawerParams.caseBehaviorId,
          behaviorData: this.behaviorData,
          selectedRow: data,
          selectedUnitName: this.drawerParams.selectedUnitName,
          onDataSaved: () => this.getDataSheetData(),
          frequencyDrawerTitleTemplate: this
            .drawerTitleFrequency as unknown as TemplateRef<object>,
          durationDrawerTitleTemplate: this
            .drawerTitleDuration as unknown as TemplateRef<object>,
        },
        DRAWER_IDS.DURATION
      )
      .afterClose.subscribe(() => {
        this.getDataSheetData();
      });
  }

  openEnterDataDrawer(data: DatasheetDataItem) {
    this.drawerService
      .openDrawer(
        PartialIntervalComponent,
        this.drawerTitlePartialInterval as unknown as TemplateRef<object>,
        520,
        true,
        undefined,
        {
          caseDetail: this.drawerParams.caseDetail,
          selectedUnit: this.drawerParams.selectedUnit,
          selectedDate: data.date,
          selectedDay: data.day,
          scheduleData: this.scheduleData,
          unitId: this.behaviorData.id,
          caseBehaviorId: this.drawerParams.caseBehaviorId,
          behaviorData: this.behaviorData,
          selectedRow: data,
          selectedUnitName: this.drawerParams.selectedUnitName,
        },
        DRAWER_IDS.PARTIAL_INTERVAL
      )
      .afterClose.subscribe(() => {
        this.getDataSheetData();
      });
  }

  closeFrequencyDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.FREQUENCY);
  }

  closeDurationDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.DURATION);
  }

  closePartialIntervalDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.PARTIAL_INTERVAL);
  }

  openDataNotesDrawer(data: DatasheetDataItem) {
    this.drawerService
      .openDrawer(
        ManageDataNotesComponent,
        this.drawerTitleDataNotes as unknown as TemplateRef<object>,
        1170,
        true,
        undefined,
        {
          caseDetail: this.drawerParams.caseDetail,
          selectedUnit: this.drawerParams.selectedUnit,
          selectedDate: data.date,
          selectedDay: data.day,
          scheduleData: this.scheduleData,
          unitId: this.behaviorData.id,
          caseBehaviorId: this.drawerParams.caseBehaviorId,
          behaviorData: this.behaviorData,
          selectedRow: data,
          selectedUnitName: this.drawerParams.selectedUnitName,
        },
        DRAWER_IDS.DATA_NOTES
      )
      .afterClose.subscribe(() => {
        this.getDataSheetData();
      });
  }

  closeDataNotesDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.DATA_NOTES);
  }

  openDataDrawer(data: DatasheetDataItem) {
    if (this.drawerParams.selectedUnit === 2) {
      this.openSetDurationDrawer(data);
    } else if (this.drawerParams.selectedUnit === 4) {
      this.openSetFrequencyDrawer(data);
    } else {
      this.openEnterDataDrawer(data);
    }
  }

  // Method to check if a date is in the future
  isFutureDate(dateString: string): boolean {
    const currentDate = new Date();
    const dataDate = new Date(dateString);
    return dataDate > currentDate;
  }

  // Helper method to get the next N weekdays (skipping weekends)
  private getNextWeekdays(
    startDate: Date,
    count: number,
    forward: boolean
  ): Date[] {
    const dates: Date[] = [];
    let daysOffset = 0;
    const direction = forward ? 1 : -1;

    while (dates.length < count) {
      daysOffset += direction;
      const checkDate = new Date(startDate);
      checkDate.setDate(startDate.getDate() + daysOffset);
      const dayOfWeek = checkDate.getDay();
      // Skip Saturday (6) and Sunday (0)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        dates.push(new Date(checkDate));
      }
    }

    return dates;
  }

  prevWeek() {
    const rangeLength = this.weekDays?.length > 0 ? this.weekDays.length : 7;
    const currentStartDate = new Date(this.weekDays[0].date + 'T00:00:00');

    // For 1-2 day ranges, skip weekends
    if (rangeLength <= 2) {
      const dates = this.getNextWeekdays(currentStartDate, rangeLength, false);
      dates.reverse(); // Reverse to get chronological order

      const prevStart = dates[0];
      const prevEnd = dates.at(-1)!;

      this.weekRange = [prevStart, prevEnd];
      this.weekDays = this.getCurrentWeekDates(prevStart, prevEnd);
    } else {
      // Original logic for ranges > 2 days
      const prevEnd = new Date(currentStartDate);
      prevEnd.setDate(currentStartDate.getDate() - 1);
      const prevStart = new Date(prevEnd);
      prevStart.setDate(prevEnd.getDate() - (rangeLength - 1));

      this.weekRange = [prevStart, prevEnd];
      this.weekDays = this.getCurrentWeekDates(prevStart, prevEnd);
    }

    this.getDataSheetData();
  }

  nextWeek() {
    if (!this.isCurrentWeek()) {
      const rangeLength = this.weekDays?.length > 0 ? this.weekDays.length : 7;
      const lastWeekDay = this.weekDays.at(-1);
      if (!lastWeekDay) return;

      const currentEndDate = new Date(lastWeekDay.date + 'T00:00:00');

      // For 1-2 day ranges, skip weekends
      if (rangeLength <= 2) {
        const dates = this.getNextWeekdays(currentEndDate, rangeLength, true);

        const nextStart = dates[0];
        const nextEnd = dates.at(-1)!;

        this.weekRange = [nextStart, this.clampToToday(nextEnd)];
        this.weekDays = this.getCurrentWeekDates(nextStart, nextEnd);
      } else {
        // Original logic for ranges > 2 days
        const nextStart = new Date(currentEndDate);
        nextStart.setDate(currentEndDate.getDate() + 1);
        const nextEnd = new Date(nextStart);
        nextEnd.setDate(nextStart.getDate() + (rangeLength - 1));

        this.weekRange = [nextStart, this.clampToToday(nextEnd)];
        this.weekDays = this.getCurrentWeekDates(nextStart, nextEnd);
      }

      this.getDataSheetData();
    }
  }

  isCurrentWeek(): boolean {
    return this.weekDays.some(day => day.date === this.currentWeekStart);
  }

  modify() {
    this.isModify = true;
  }

  finish() {
    this.drawerService.closeDrawer(DRAWER_IDS.MANAGE_DATA);
    this.drawerService.closeDrawer(DRAWER_IDS.ENTER_DATA);
  }

  applyModify() {
    // Snapshot original data before any modification
    const originalData = structuredClone(this.dataSheetDataItems);

    // Replace '0' with an empty string in totalFrequency
    const updatedRecords = _.map(this.dataSheetDataItems, item =>
      _.assign({}, item, {
        totalFrequency: item.totalFrequency === '' ? '' : item.totalFrequency,
      })
    );

    // Filter records where totalFrequency is NOT an empty string and isUpdateTotal is true
    const filteredRecords = _.filter(
      updatedRecords,
      item => item.totalFrequency !== '' && item.isUpdateTotal
    );

    // Map the filtered records into the desired structure
    const blockValues = filteredRecords.map(item => ({
      id: item.id,
      dataDate: item.date,
      unitValue: Number.parseInt(item.totalFrequency, 10),
    }));

    const modifiedFields = filteredRecords.map(item => {
      const originalItem = originalData.find(d => d.date === item.date);
      return this.auditContextService.buildFieldChanges(
        { totalFrequency: originalItem?.oldTotalFrequency ?? null },
        { totalFrequency: item.totalFrequency },
        {
          sensitiveFields: [],
          labelMap: {
            totalFrequency: `Total Frequency | ${this.drawerParams.selectedUnitName ?? ''} (${item.date})`,
          },
          excludeFields: [],
        }
      )[0];
    });

    const payload: BlockValueModel = {
      unitId: this.behaviorData.id,
      blockValues: blockValues,
      _modifiedFields: modifiedFields,
    };
    this.saveBlockValue(payload);
  }
  cancelModify() {
    this.getDataSheetData();
    this.isModify = false;
  }

  get totalTitle(): string {
    return `Total ${this.drawerParams.selectedUnit === 2 ? 'Latency' : 'Duration'}`;
  }
  get avgTitle(): string {
    return `Average ${this.drawerParams.selectedUnit === 2 ? 'Latency' : 'Duration'}`;
  }
  saveBlockValue(payload: BlockValueModel) {
    this.loaderService.show();
    const operation$ = this.insightsService.saveBlockValue(payload);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: () => {
          this.commonService.notify('success', `Datasheet saved successfully`);
          this.isModify = false;
          this.getDataSheetData();
        },
        error: error => {
          this.loaderService.hide();
          this.handleError(error);
        },
      });
  }

  private handleError({ errors, message }: ErrorResponse): void {
    const errorMessages = errors.map(
      err => err.description || err.detailedMessage
    );
    this.commonService.notify('error', errorMessages, message);
  }

  getUnitValues(univalues: string[]): string {
    return univalues
      .map(row => row?.trim() || '') // Replace empty values with "N/A"
      .join(', '); // Join with a single comma
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  delete(data: DatasheetDataItem) {
    if (!this.allowDeleteDataEntry) {
      return;
    }
    const [year, month, day] = data.date.split('-');
    const dataDate = `${month}/${day}/${year}`;
    this.deleteMsg = `Are you sure you want to delete the data and notes recorded for ${dataDate}?`;
    this.showConfirmDialog = true;
    this.deleteObj = {
      id: this.behaviorData.id,
      date: data.date,
    };
    data.totalDuration = '';
    data.totalFrequency = '';
    data.averageDuration = '';
  }

  removeUnitValue() {
    if (this.behaviorData.id) {
      this.isDeleting = true;
      const operation$ = this.insightsService.deleteUnitValue(
        this.deleteObj.id,
        this.deleteObj.date
      );
      operation$
        .pipe(
          take(1),
          takeUntil(this.destroy$),
          finalize(() => {
            this.isDeleting = false;
          })
        )
        .subscribe({
          next: () => {
            this.getDataSheetData();
            this.isDeleting = false;
            this.showConfirmDialog = false;
            this.commonService.notify(
              'success',
              'Record deleted successfully.'
            );
            this.cdr.detectChanges();
          },
          error: error => {
            this.isDeleting = false;
            this.showConfirmDialog = false;
            this.handleError(error);
          },
        });
    }
  }

  handleCancel() {
    this.showConfirmDialog = false;
    this.isDeleting = false;
  }

  onWeekRangeChange(range: Date[]) {
    if (range?.length !== 2) return;

    // Normalize dates to start of day to avoid timezone/time issues
    const startDate = startOfDay(range[0]);
    const endDate = startOfDay(range[1]);

    const diffInMs = endDate.getTime() - startDate.getTime();
    const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24)) + 1; // inclusive
    if (diffInDays > 30) {
      this.rangeErrorMsg = 'You can select a maximum of 30 days.';
      return;
    }

    this.rangeErrorMsg = '';

    // For 1-2 day ranges, skip weekends
    if (diffInDays <= 2) {
      const weekdayDates: Date[] = [];
      const currentDate = new Date(range[0]);
      const endDate = new Date(range[1]);

      // Collect weekdays within the selected range
      while (currentDate <= endDate) {
        const dayOfWeek = currentDate.getDay();
        // Skip Saturday (6) and Sunday (0)
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          weekdayDates.push(new Date(currentDate));
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // If no weekdays in selected range, get next weekdays
      if (weekdayDates.length === 0) {
        const nextWeekdays = this.getNextWeekdays(
          range[0],
          Math.floor(diffInDays),
          true
        );
        const adjustedStart = nextWeekdays[0];
        const adjustedEnd = nextWeekdays.at(-1)!;
        this.weekRange = [adjustedStart, adjustedEnd];
        this.weekDays = this.getCurrentWeekDates(adjustedStart, adjustedEnd);
      } else {
        // Use the weekdays found in the range
        const adjustedStart = weekdayDates[0];
        const adjustedEnd = weekdayDates.at(-1)!;
        this.weekRange = [adjustedStart, adjustedEnd];
        this.weekDays = this.getCurrentWeekDates(adjustedStart, adjustedEnd);
      }
    } else {
      // Original logic for ranges > 2 days
      this.weekDays = this.getCurrentWeekDates(range[0], range[1]);
      this.weekRange = range;
    }

    this.getDataSheetData();
  }
}
